<script setup lang="ts">
  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import { useApiNew } from '@/shared/composables/useApi'
  import { useOCRAvailability } from '@/shared/composables/useOCRAvailability'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import type { VisitorType } from '@/shared/types/store.types'
  import AlertQC from '@qicgroupdoha/qic-connect-ds/components/AlertQC'
  import LoaderQC from '@qicgroupdoha/qic-connect-ds/components/LoaderQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import { storeToRefs } from 'pinia'
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useOcrFlow } from './composables/useOcrFlow'
  import ContactsBlock from './components/ContactsBlock.vue'
  import { useVisitorAnalytics } from './composables/useVisitorAnalytics'
  import { useEmailDomains } from './composables/useEmailDomains'
  import FormFooter from './components/FormFooter.vue'
  import OcrUploader from './components/OcrUploader.vue'
  import NationalityCard from './components/NationalityCard.vue'
  import NationalityChangeModal from './components/NationalityChangeModal.vue'
  import OcrRemoveModal from './components/OcrRemoveModal.vue'
  import { useFindCountries } from '@/shared/composables/useFindCountries'
  import { usePersonState } from './composables/usePersonState'
  import { useOcrSignals } from './composables/useOcrSignals'
  import { useVisitorValidation } from './composables/useVisitorValidation'
  import VisaReadOnlyBlock from './components/VisaReadOnlyBlock.vue'
  import PassportDetailsFields from './components/PassportDetailsFields.vue'

  interface Props {
    visitor: VisitorType
    isHolder?: boolean
    isEdit?: boolean
    isLoading?: boolean
    emailIsValid?: boolean
  }

  interface Emits {
    (e: 'save', value: VisitorType): void
    (e: 'setEmailIsValid'): void
    (e: 'delete', value: VisitorType): void
    (e: 'openRemoveWindowEvent', value: VisitorType): void
    (e: 'closeRemoveWindowEvent', value: VisitorType): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isHolder: false,
    isEdit: false,
    isLoading: false,
    emailIsValid: false,
  })
  const emit = defineEmits<Emits>()

  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const { t } = useI18n()
  const { findCountries } = useFindCountries()
  const { relations, maritalStatuses } = storeToRefs(coreStore)

  const privacyPolicyLink = ref('')
  const showRemoveWindow = ref(false)
  const contactsBlockRef = ref<InstanceType<typeof ContactsBlock>>()
  const phoneIsCompleted = ref(false)

  const personState = usePersonState({
    initialVisitor: props.visitor,
    isHolder: props.isHolder,
    isEdit: props.isEdit,
  })

  const { isOcrAvailable } = useOCRAvailability()
  const signals = useOcrSignals({
    nationalityIso: personState.nationalityIso,
    isOcrAvailable,
  })

  const { v$, errors, emailError, resetOcrData } = useVisitorValidation({
    person: personState.person,
    selectedCountry: personState.selectedCountry,
    isDisabledProperties: personState.isDisabledProperties,
    isHolder: props.isHolder,
    isMaritalStatusEnabled: personState.isMaritalStatusEnabled,
    isNotOcrManualMode: signals.isNotOcrManualMode,
    ocrUploadResult: signals.ocr.uploadResult,
    emailIsValid: () => props.emailIsValid,
    passportMinDate: personState.passportMinDate,
  })

  const {
    isHolderInfoPage,
    trackPrivacyPolicy,
    handleSelectedDomain,
    sendCloseOcrModal,
    sendClickUploader,
    sendViewOcrBlock,
  } = useVisitorAnalytics({ isEdit: props.isEdit, isHolder: props.isHolder })

  const ocrFlow = useOcrFlow({
    isAvailable: isOcrAvailable,
    signals,
    person: personState.person,
    selectedNationality: personState.selectedNationality,
    isHolder: props.isHolder,
    resetOcrData,
    resetValidation: () => v$.value.$reset(),
    touchPassportExpiry: () => v$.value.passportExpiryDate.$touch(),
    onSendCloseOcrModal: sendCloseOcrModal,
  })

  // Pull names into template scope
  const {
    person,
    selectedCountry,
    selectedNationality,
    localCountries,
    localNationalities,
    isDisabledProperties,
    isMaritalStatusEnabled,
    passportMinDate,
    setSelectedCountry,
    setSelectedNationality,
    setPhoneInfo,
  } = personState
  const { ocr, isOcrManualMode, isFileUploaderShown, isNotOcrManualMode } = signals
  const {
    isNationalityChangeModalVisible,
    isRemoveOcrModalVisible,
    previousSelectedNationality,
    isNationalityChangedOnClose,
    toggleOcrManualMode,
    checkOcrManualMode,
    checkOcrNationality,
    openNationalityChangeModal,
    openRemoveOcrModal,
    closeNationalityChangeModal,
    closeRemoveOcrModal,
    handleProcessFile,
    clickUploader,
  } = ocrFlow

  const fieldsMode = computed<'default' | 'ocr' | 'visa'>(() => {
    if (isDisabledProperties) return 'visa'
    if (isOcrAvailable) return 'ocr'
    return 'default'
  })

  const showPassportDetails = computed(() => {
    return (
      (isFileUploaderShown.value && (isOcrManualMode.value || ocr.isProcessing.value)) ||
      !isFileUploaderShown.value ||
      ocr.uploadResult.value === 'success'
    )
  })

  const localFindNationality = (searchString: string) =>
    findCountries(searchString, localNationalities.value)
  const localFindCountry = (searchString: string) =>
    findCountries(searchString, localCountries.value)

  const btnText = computed(() => {
    if (isHolderInfoPage.value) {
      return isOcrAvailable ? t('quote_proceed') : t('qlm_proceed')
    }

    return person.value.srNo > 0 ? t('qlm_update') : t('qlm_add_visitor')
  })

  const headerTitle = computed(() => {
    if (!props.isHolder && isOcrAvailable) return undefined
    if (isOcrAvailable)
      return props.isEdit
        ? t('qlm_personal_info_header_edit')
        : t('visitors.fill_visitors_info.personal_info_header')
    return props.isHolder ? t('qlm_personal_info_header') : t('personal_info.additional.header')
  })

  const nextFocus = (event: Event) => {
    if ((event as KeyboardEvent).code === 'Enter') {
      const curTabindex = (event.target as HTMLInputElement)?.tabIndex
      const nextTabindex = document.querySelector(`[tabindex='${curTabindex + 1}']`) as HTMLElement
      if (nextTabindex) {
        nextTabindex.focus()
      }
      ;(event.target as HTMLInputElement)?.blur()
    }
  }

  const submit = () => {
    if (ocr.isProcessing.value) return

    if (!ocr.uploadResult.value && isNotOcrManualMode.value) {
      ocr.uploadResult.value = 'error'
    }

    v$.value.$touch()
    if (v$.value.$error) return

    const relationship = relations.value.find((item) => item.code === person.value.relationship)
    const nationality = coreStore.nationalities?.find(
      (item) => item.code === person.value.nationality
    )

    if (relationship) {
      person.value.relationshipName = relationship.desc
    }

    if (nationality) {
      person.value.nationalityName = nationality.desc
    }
    emit('save', person.value)
  }

  const switchRemoveWindow = (action: 'open' | 'close', person: VisitorType) => {
    showRemoveWindow.value = !showRemoveWindow.value
    return action === 'open'
      ? emit('openRemoveWindowEvent', person)
      : emit('closeRemoveWindowEvent', person)
  }

  const {
    filtredEmailDomains,
    emailDomainsLoading,
    loadEmailDomains,
    onInputEmail: onInputEmailRaw,
  } = useEmailDomains()

  const onInputEmail = (value: string | number | null) => {
    emit('setEmailIsValid')
    onInputEmailRaw(value)
  }

  onMounted(async () => {
    const forms = document.querySelectorAll('input')
    forms.forEach((el) => {
      el.addEventListener('keydown', nextFocus)
    })
    coreStore.getDurations()
    privacyPolicyLink.value = await useApiNew().commonSeervice.getPrivacyPolicyLink()
    await loadEmailDomains()

    if (props.isEdit) {
      toggleOcrManualMode()
    }
    nextTick(checkOcrManualMode)
  })

  onUnmounted(() => {
    const forms = document.querySelectorAll('input')
    forms.forEach((el) => {
      el.removeEventListener('keydown', nextFocus)
    })
  })

  watch(
    isNotOcrManualMode,
    (shown) => {
      if (shown) sendViewOcrBlock()
    },
    { immediate: true }
  )

  watch(
    selectedNationality,
    (newValue, oldValue) => {
      if (ocr.isProcessing.value || ocr.uploadResult.value === 'success') {
        if (props.isHolder) return
        if (isNationalityChangedOnClose.value) {
          isNationalityChangedOnClose.value = false
          return
        }
        if (oldValue?.code !== newValue?.code) {
          previousSelectedNationality.value = oldValue
          openNationalityChangeModal()
        }
      }
      if (!isNationalityChangeModalVisible.value) {
        person.value.nationality = selectedNationality.value?.code ?? null
      }
      v$.value.nationality.$reset()
    },
    {
      flush: 'post',
    }
  )

  // Phone-sync side-effect for selectedCountry (country + validation-reset handled in composable)
  watch(
    () => selectedCountry.value,
    () => {
      if (!phoneIsCompleted.value && selectedCountry.value?.iso) {
        contactsBlockRef.value?.visitorsPhoneInput?.setCountryCode(
          selectedCountry.value.iso.toUpperCase()
        )
      }
    }
  )

  watch(
    () => props.visitor,
    (newValue) => {
      if (!newValue || !newValue.firstName?.length) return
      person.value = { ...newValue }
      person.value.fullName = `${newValue.firstName} ${newValue.lastName}`.trim()
      if (checkOcrNationality(newValue.nationality) && visitorsStore.lastHolderNationality) {
        setSelectedNationality(visitorsStore.lastHolderNationality)
        person.value.nationality = visitorsStore.lastHolderNationality
      } else {
        setSelectedNationality(newValue.nationality)
      }
      setSelectedCountry(newValue.country)
      person.value.passportExpiryDate = person.value.passportExpiryDate
        ? new Date(person.value.passportExpiryDate)
        : null
      if (
        person.value.passportExpiryDate !== null &&
        person.value.passportExpiryDate < passportMinDate.value
      ) {
        person.value.passportExpiryDate = null
      }
      checkOcrManualMode()
    },
    {
      flush: 'post',
      once: true,
    }
  )

  const handleUploaderClick = () => {
    clickUploader(() => sendClickUploader('click_upload_doc'))
  }

  const onBeforeCancel = (): Promise<boolean> => {
    sendClickUploader('click_icon', 'clear')
    return openRemoveOcrModal()
  }
</script>

<template>
  <WrapperQC direction="column" gap="var(--spacing-8)">
    <NationalityCard
      v-if="!isHolder && isOcrAvailable"
      v-model:relationship="person.relationship"
      v-model:selected-nationality="selectedNationality"
      :local-nationalities="localNationalities"
      :relations="relations"
      :errors="errors"
      :v$="v$"
      :disabled-nationality="isDisabledProperties"
      :find-nationality="localFindNationality"
      :is-loading="isLoading"
      :ocr-is-available="isOcrAvailable"
    />

    <FlowCard
      class="visitor-form"
      :class="{
        'visitor-form--loading': props.isLoading,
      }"
      :title="headerTitle"
      :with-button="false"
      :padding="isOcrAvailable ? 'var(--spacing-8)' : 'var(--spacing-9)'"
    >
      <template v-if="(!isEdit && isHolder) || (!isHolder && !isOcrAvailable)" #subtitle>
        <p
          v-if="!isEdit && isHolder"
          class="visitor-form__main-annotation"
          :class="{
            'text-medium': !$qicConnect.isMobile,
            'text-regular': $qicConnect.isMobile,
          }"
        >
          {{ $t('travel_dates.policy_holder.annotation_1') }}
          {{ $t('travel_dates.policy_holder.annotation_2') }}
          {{ $t('travel_dates.policy_holder.annotation_3') }}
        </p>
        <p
          v-else-if="!isHolder && !isOcrAvailable"
          class="visitor-form__annotation"
          :class="{
            'text-medium': !$qicConnect.isMobile,
            'text-regular': $qicConnect.isMobile,
          }"
        >
          {{ $t('qic_personal_info_annotation') }}
        </p>
      </template>

      <div class="visitor-form__content">
        <!-- @vue-ignore -->
        <OcrUploader
          v-if="isFileUploaderShown"
          v-model:upload-result="ocr.uploadResult.value"
          :error-message="ocr.errorMessage.value"
          :upload-result-errors="errors.uploadResult"
          :is-ocr-manual-mode="isOcrManualMode"
          @file-selected="handleProcessFile"
          @cancel="ocr.cancelRequest(resetOcrData)"
          @error:file-size="ocr.setErrorFileSize"
          @click="handleUploaderClick"
          @toggle-manual-mode="toggleOcrManualMode"
          @before-cancel="onBeforeCancel"
        />

        <AlertQC
          v-if="!isOcrAvailable && isDisabledProperties"
          variant="success"
          :subtitle="$t('qic_personal_info_additional_annotation')"
          with-icon
        />

        <div class="visitor-form__person-details">
          <PassportDetailsFields
            v-if="showPassportDetails"
            v-model:selected-country="selectedCountry"
            v-model:selected-nationality="selectedNationality"
            v-model:first-name="person.firstName"
            v-model:last-name="person.lastName"
            v-model:full-name="person.fullName"
            v-model:birth-date="person.birthDate"
            v-model:gender="person.gender"
            v-model:passport="person.passport"
            v-model:passport-expiry-date="person.passportExpiryDate"
            v-model:marital-status="person.maritalStatus"
            v-model:relationship="person.relationship"
            :mode="fieldsMode"
            :v$="v$"
            :errors="errors"
            :is-holder="isHolder"
            :ocr-is-available="isOcrAvailable"
            :ocr-processing="ocr.isProcessing.value"
            :ocr-upload-success="ocr.uploadResult.value === 'success'"
            :is-file-uploader-shown="isFileUploaderShown"
            :is-ocr-manual-mode="isOcrManualMode"
            :is-marital-status-enabled="isMaritalStatusEnabled"
            :is-disabled-properties="isDisabledProperties"
            :local-countries="localCountries"
            :local-nationalities="localNationalities"
            :relations="relations"
            :marital-statuses="maritalStatuses"
            :passport-min-date="passportMinDate"
            :find-country="localFindCountry"
            :find-nationality="localFindNationality"
          />

          <VisaReadOnlyBlock
            v-if="isDisabledProperties"
            :visa="person.visa"
            :visa-validity-date="person.visaValidityDate"
          />

          <ContactsBlock
            v-if="!isOcrAvailable"
            ref="contactsBlockRef"
            v-model:email="person.email"
            v-model:contacts-same="person.contactsSame"
            :visitor="person"
            :email-error="emailError"
            :email-is-valid="!v$.email.$invalid"
            :filtred-email-domains="filtredEmailDomains"
            :email-domains-loading="emailDomainsLoading"
            :is-holder="isHolder"
            :is-edit="isEdit"
            :title-text="$t('qlm_contacts')"
            title-margin-top="var(--spacing-5)"
            @input-email="onInputEmail"
            @selected-domain="handleSelectedDomain"
            @phone-completed="phoneIsCompleted = $event"
            @phone-changed="setPhoneInfo"
          />
        </div>
      </div>

      <template v-if="!isOcrAvailable">
        <FormFooter
          :btn-text="btnText"
          :show-remove-button="!isHolder && isEdit"
          :show-remove-window="showRemoveWindow"
          :privacy-policy-link="privacyPolicyLink"
          @submit="submit"
          @open-remove="switchRemoveWindow('open', person)"
          @close-remove="switchRemoveWindow('close', person)"
          @delete="emit('delete', person)"
          @track-privacy-policy="trackPrivacyPolicy"
        />
      </template>
    </FlowCard>

    <FlowCard
      v-if="isOcrAvailable"
      padding="var(--spacing-8)"
      :with-button="false"
      class="visitor-form"
      :class="{
        'visitor-form--loading': props.isLoading,
      }"
    >
      <ContactsBlock
        ref="contactsBlockRef"
        v-model:email="person.email"
        v-model:contacts-same="person.contactsSame"
        :visitor="person"
        :email-error="emailError"
        :email-is-valid="!v$.email.$invalid"
        :filtred-email-domains="filtredEmailDomains"
        :email-domains-loading="emailDomainsLoading"
        :is-holder="isHolder"
        :is-edit="isEdit"
        :title-text="$t('visitors.fill_visitors_info.contacts.heading.text')"
        @input-email="onInputEmail"
        @selected-domain="handleSelectedDomain"
        @phone-completed="phoneIsCompleted = $event"
        @phone-changed="setPhoneInfo"
      />
      <FormFooter
        :btn-text="btnText"
        wide
        :show-remove-button="!isHolder && isEdit"
        :show-remove-window="showRemoveWindow"
        :privacy-policy-link="privacyPolicyLink"
        @submit="submit"
        @open-remove="switchRemoveWindow('open', person)"
        @close-remove="switchRemoveWindow('close', person)"
        @delete="emit('delete', person)"
        @track-privacy-policy="trackPrivacyPolicy"
      />

      <div v-if="isLoading && !isOcrAvailable" class="visitor-form__loader">
        <LoaderQC size="extraLarge" variant="primary" />
      </div>
    </FlowCard>

    <div v-if="isLoading" class="visitor-form__loader">
      <LoaderQC size="extraLarge" variant="primary" />
    </div>

    <NationalityChangeModal
      v-if="isNationalityChangeModalVisible"
      @keep="(close) => closeNationalityChangeModal(close, true)"
      @change="(close) => closeNationalityChangeModal(close, false)"
      @close="closeNationalityChangeModal(undefined, true)"
    />
    <OcrRemoveModal
      v-if="isRemoveOcrModalVisible"
      @keep="(close) => closeRemoveOcrModal(close, true)"
      @remove="(close) => closeRemoveOcrModal(close, false)"
      @close="closeRemoveOcrModal(undefined, true)"
    />
  </WrapperQC>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';

  .visitor-form {
    /* TODO: Temporary fix for 8.0.1 release. It should be fixed in qic-connect */
    :deep(.ds-base-input__wrapper) {
      max-height: 56px;
    }

    position: relative;

    &__annotation {
      text-align: center;
      @media (max-width: 550px) {
        font-size: 14px;
      }
    }

    &__main-annotation {
      text-align: center;
    }

    &-datepicker {
      z-index: 5;
    }

    &-select {
      &--nationalities {
        z-index: 4 !important;
      }
      &--country {
        z-index: 3 !important;
      }
    }

    &.visitor-form--loading {
      :deep(.visitor-form__footer),
      :deep(.visitor-form__inputs),
      :deep(.visitor-form__contacts),
      :deep(.visitor-form__uploader),
      :deep(.visitor-form__uploader-divider) {
        opacity: 0.2;
      }
    }
  }

  .visitor-form__person-details {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);

    &:empty {
      display: none;
    }
  }

  .visitor-form__content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);
  }

  .visitor-form__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .country-selector__country-flag {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }
</style>
